/**
 * 🎨 CENTRALIZED COLOR SYSTEM - Modern SaaS Design (2025/2026)
 * Unified color system for the entire application
 * Modern palette inspired by Linear, Vercel, Stripe, Notion
 * 
 * COLOR PHILOSOPHY:
 * - Primary (blue/cyan): Actions, navigation, links, interactive elements
 * - Emerald/Green: Success, completed, paid, positive feedback
 * - Amber/Yellow: Pending, warnings, attention needed
 * - Red/Rose: Errors, danger, overdue, destructive actions
 * - Violet/Purple: In production, special states
 * - Slate/Gray: Neutral, archived, disabled, backgrounds
 * 
 * Note: "blue" and "primary" are used interchangeably - they're the same color family (sky blue)
 */

// 🎯 LEAD STATUS COLORS - Flat tinted chips (semantic color without candy gradients)
export const leadStatusColors = {
  'to_contact': 'bg-blue-100 text-blue-800 border border-blue-200',
  'pending': 'bg-amber-100 text-amber-800 border border-amber-200',
  'contacted': 'bg-amber-100 text-amber-800 border border-amber-200',
  'negotiation': 'bg-orange-100 text-orange-800 border border-orange-200',
  'completed': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  'archived': 'bg-slate-100 text-slate-700 border border-slate-200',
  'rejected': 'bg-red-100 text-red-800 border border-red-200',
  'abandoned': 'bg-red-50 text-red-700 border border-red-100',
  'in_production': 'bg-violet-100 text-violet-800 border border-violet-200',
} as const;

// 🎯 PAYMENT STATUS COLORS - Flat tinted chips
export const paymentStatusColors = {
  'paid': {
    badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
    hex: '#10b981',                        // emerald-500
    stroke: '#059669',                     // emerald-600
  },
  'sent': {
    badge: 'bg-amber-100 text-amber-800 border border-amber-200',
    hex: '#f59e0b',                        // amber-500
    stroke: '#d97706',                     // amber-600
  },
  'overdue': {
    badge: 'bg-red-100 text-red-800 border border-red-200',
    hex: '#ef4444',                        // red-500
    stroke: '#dc2626',                     // red-600
  },
} as const;


// 🎯 KANBAN COLUMN GRADIENT COLORS - Modern gradient bars for column headers
export const kanbanGradientColors = {
  BRIEF_RECEIVED: 'from-blue-500 to-cyan-500',          // blue → cyan
  IN_PRODUCTION: 'from-violet-500 to-purple-500',       // violet → purple
  V1_SENT: 'from-amber-500 to-yellow-500',              // amber → yellow
  CHANGES_REQUESTED: 'from-orange-500 to-rose-500',     // orange → rose
  DELIVERED: 'from-emerald-500 to-green-500',           // emerald → green
  ARCHIVED: 'from-slate-400 to-gray-400',               // slate → gray
  // Follow-up section colors (using Kanban gradient style)
  OVERDUE: 'from-orange-500 to-rose-500',               // orange → rose (matches CHANGES_REQUESTED)
  TODAY: 'from-blue-500 to-cyan-500',                   // blue → cyan (matches BRIEF_RECEIVED)
} as const;

// 🎯 USAGE BAR COLORS - Pastel colors for quota usage bars
export const usageBarColors = {
  low: 'bg-emerald-400',        // Green for low usage (< 70%)
  medium: 'bg-amber-400',       // Amber/Orange for medium usage (70-90%)
  high: 'bg-red-400',           // Red for high usage (≥ 90%)
  unlimited: 'bg-emerald-400',  // Green for unlimited plans
} as const;

// 🎨 NAVIGATION COLORS - Modern, sleek navigation
export const navigationColors = {
  // Active navigation item — follows Appearance accent
  active: {
    background: 'bg-primary-600',
    text: 'text-white',
    shadow: 'shadow-sm shadow-primary-500/25',
  },
  // Inactive navigation item - Softer, more readable
  inactive: {
    text: 'text-slate-400',
    hoverBackground: 'hover:bg-slate-800/60',
    hoverText: 'hover:text-white',
  },
  // Sidebar background — original solid dark rail (unchanged by accent)
  sidebar: {
    background: 'bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950',
    border: 'border-slate-800/60',
    borderHeader: 'border-slate-800/50',
  },
  // Settings tabs - Modern, clean design (follows Appearance accent)
  settingsTab: {
    active: {
      background: 'bg-primary-50',
      text: 'text-primary-700',
      border: 'border border-primary-200/60',
      shadow: 'shadow-sm',
      iconBackground: 'bg-primary-100',
      iconText: 'text-primary-600',
      indicator: 'bg-primary-600',
    },
    inactive: {
      text: 'text-slate-600',
      hoverBackground: 'hover:bg-slate-50',
      hoverText: 'hover:text-slate-900',
      hoverShadow: 'hover:shadow-sm',
      iconBackground: 'bg-slate-100',
      iconText: 'text-slate-500',
      iconHoverBackground: 'group-hover:bg-slate-200',
      iconHoverText: 'group-hover:text-slate-600',
    },
  },
} as const;

// 🎨 AUTH / PRIMARY ACTIONS — solid in-app; soft gradient for marketing CTAs
export const authColors = {
  /** In-app primary fills (sidebar CTAs, etc.) */
  primarySolid: 'bg-primary-600 hover:bg-primary-700',
  /** Marketing / landing CTAs only — soft gradient */
  primaryGradient:
    'bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 hover:from-primary-600 hover:via-primary-700 hover:to-primary-800',
  // Primary auth button (login, signup) - solid, quieter than marketing CTAs
  primaryButton:
    'bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-3 rounded-xl transition-colors duration-200 shadow-sm hover:shadow-md',
  // Auth links (forgot password, create account)
  authLink: 'text-primary-600 hover:text-primary-700 cursor-pointer transition-colors',
  // Auth background — soft primary tint
  backgroundGradient: 'bg-gradient-to-br from-slate-50 via-primary-50/30 to-primary-50/20',
  // Auth icon fill
  iconGradient: 'bg-primary-600',
} as const;

// 🎨 SHADOWS - Quiet resting elevation; paper for floating UI only
export const shadows = {
  // Base shadows
  none: 'shadow-none',
  subtle: 'shadow-sm',
  medium: 'shadow-md',
  large: 'shadow-lg',
  xl: 'shadow-xl',
  // Interactive shadows (in-app controls)
  interactive: 'shadow-sm hover:shadow-md transition-shadow',
  // Floating panels (menus / popovers) — prefer Tailwind `shadow-paper`
  floating: 'shadow-paper',
  // Colored shadows - matching button/action colors (kept light)
  colored: {
    primary: 'shadow-md shadow-primary-500/20',
    blue: 'shadow-md shadow-blue-500/20',
    cyan: 'shadow-md shadow-cyan-500/20',
    purple: 'shadow-md shadow-purple-500/20',
    violet: 'shadow-md shadow-violet-500/20',
    green: 'shadow-md shadow-emerald-500/20',
    emerald: 'shadow-md shadow-emerald-500/20',
    amber: 'shadow-md shadow-amber-500/20',
    red: 'shadow-md shadow-red-500/20',
  },
} as const;

/**
 * Radius + elevation language:
 * - Surfaces (cards, inputs, modals, panels): rounded-xl + shadow-sm
 * - Controls (buttons): rounded-lg
 * - Floating UI (menus, select lists): rounded-xl + shadow-paper
 */
export const surfaces = {
  radius: 'rounded-xl',
  radiusControl: 'rounded-lg',
  elevation: 'shadow-sm',
  elevationHover: 'hover:shadow-md',
  floating: 'shadow-paper',
} as const;

// 🎨 CLICKABLE LINKS - Modern blue, better contrast
export const clickableLinks = 'text-primary-600 hover:text-primary-700 cursor-pointer transition-colors';

// 🎨 ALERT/FEEDBACK BOXES - For messages, tips, alerts, and feedback
// Consistent styling across all alert types (info, success, warning, error)
export const alertBox = {
  // Info - Blue: Tips, hints, informational messages
  info: {
    container: 'bg-blue-50 rounded-xl border border-blue-100',
    icon: 'text-blue-600',
    text: 'text-blue-700',
    title: 'text-blue-800 font-semibold',
  },
  // Success - Green: Confirmations, completed actions
  success: {
    container: 'bg-emerald-50 rounded-xl border border-emerald-100',
    icon: 'text-emerald-600',
    text: 'text-emerald-700',
    title: 'text-emerald-800 font-semibold',
  },
  // Warning - Amber: Attention needed, potential issues
  warning: {
    container: 'bg-amber-50 rounded-xl border border-amber-100',
    icon: 'text-amber-600',
    text: 'text-amber-700',
    title: 'text-amber-800 font-semibold',
  },
  // Error - Red: Errors, failures, destructive warnings
  error: {
    container: 'bg-red-50 rounded-xl border border-red-100',
    icon: 'text-red-600',
    text: 'text-red-700',
    title: 'text-red-800 font-semibold',
  },
} as const;

// Shorthand for backward compatibility
export const infoBox = alertBox.info;

// 🎨 FULL BUTTONS - colors/shadows only; radius comes from Button (`rounded-lg`)
export const fullButtons = {
  primary:
    'text-white font-medium transition-colors duration-200 shadow-sm hover:shadow-md bg-primary-600 hover:bg-primary-700',
  secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-colors border border-slate-200',
  danger: 'bg-red-500 hover:bg-red-600 text-white font-medium transition-colors shadow-sm shadow-red-500/20',
  success: 'bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors shadow-sm shadow-emerald-500/20',
  warning: 'bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors shadow-sm shadow-amber-500/20',
  info: 'bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors shadow-sm shadow-primary-500/20',
} as const;

// 🎨 ACTION BUTTONS - Modern, softer colors for icon buttons and text actions
export const actionButtons = {
  primary: 'text-primary-600 hover:text-primary-700 transition-colors',   // Edit, view, contact, project actions
  secondary: 'text-slate-500 hover:text-slate-700 transition-colors', // Secondary actions
  success: 'text-emerald-600 hover:text-emerald-700 transition-colors', // Confirm, approve
  warning: 'text-amber-600 hover:text-amber-700 transition-colors',   // Caution actions
  danger: 'text-red-500 hover:text-red-600 transition-colors',      // Delete, destructive actions
} as const;

// 🎯 FOCUS/HIGHLIGHT RING - Modern SaaS style for scroll-to-element highlighting
// Uses ring-inset to avoid clipping issues with table rows and overflow containers
export const highlightRing = {
  // Classes to apply for temporary highlight effect (used by useScrollAndHighlight DOM)
  classes: ['ring-2', 'ring-inset', 'ring-primary-500', 'bg-primary-50/70'],
  // Individual values for custom usage
  ring: 'ring-2 ring-inset ring-primary-500',
  background: 'bg-primary-50/70',
  // Combined class string (DOM / useScrollAndHighlight)
  combined: 'ring-2 ring-inset ring-primary-500 bg-primary-50/70',
  /** With shadow – for React-driven highlight (Kanban, tables). Keep as full string so Tailwind includes classes. */
  combinedWithShadow: 'ring-2 ring-inset ring-primary-500 bg-primary-50/70 shadow-lg shadow-primary-500/30',
} as const;

// 🎯 FOCUS RING - For form inputs and interactive elements
export const focusRing = {
  default: 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
  inset: 'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500',
  offset: 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
} as const;


// 🎨 NEUTRAL COLORS - Modern slate-based system (softer than gray)
export const neutralColors = {
  // Backgrounds
  white: 'bg-white',
  gray50: 'bg-slate-50',
  gray100: 'bg-slate-100',
  gray200: 'bg-slate-200',
  // Borders
  border: 'border-slate-200',
  borderLight: 'border-slate-100',
  borderDark: 'border-slate-300',
  // Text
  text: 'text-slate-600',
  textLight: 'text-slate-500',
  textMuted: 'text-slate-400',
  textDark: 'text-slate-900',
  // Combined
  default: 'bg-slate-100 text-slate-800',
  // Legacy aliases (for backward compatibility)
  grayBorder: 'border-slate-200',
  grayText: 'text-slate-600',
  grayTextLight: 'text-slate-500',
  grayTextHover: 'text-slate-900',
  grayTextDark: 'text-slate-900',
} as const;

// 🎨 BADGE COLORS - Consistent badge styling across the app
export const badgeColors = {
  // Semantic badges
  primary: 'bg-blue-100 text-blue-800 border border-blue-200/50',
  success: 'bg-emerald-100 text-emerald-800 border border-emerald-200/50',
  warning: 'bg-amber-100 text-amber-800 border border-amber-200/50',
  error: 'bg-red-100 text-red-800 border border-red-200/50',
  info: 'bg-cyan-100 text-cyan-800 border border-cyan-200/50',
  neutral: 'bg-slate-100 text-slate-700 border border-slate-200/50',
  // Special badges
  purple: 'bg-violet-100 text-violet-800 border border-violet-200/50',
  pink: 'bg-pink-100 text-pink-800 border border-pink-200/50',
} as const;

// 🎯 CALENDAR EVENT COLORS - Soft pastel colors matching badge backgrounds
// Very light and transparent pastel colors for calendar events
export const calendarEventColors = {
  // Default colors matching badge style
  blue: '#dbeafe',        // blue-100 - matches to_contact badge
  cyan: '#cffafe',        // cyan-100 - matches to_contact badge gradient
  amber: '#fef3c7',       // amber-100 - matches pending badge
  yellow: '#fef9c3',      // yellow-100 - matches pending badge gradient
  orange: '#ffedd5',      // orange-100 - matches negotiation badge
  rose: '#ffe4e6',        // rose-100 - matches negotiation/rejected badge gradient
  emerald: '#d1fae5',     // emerald-100 - matches completed badge
  green: '#dcfce7',       // green-100 - matches completed badge gradient
  red: '#fee2e2',         // red-100 - matches rejected badge
  violet: '#ede9fe',      // violet-100 - matches in_production badge
  purple: '#f3e8ff',      // purple-100 - matches in_production badge gradient
  
  // Default fallback
  default: '#dbeafe',     // blue-100
} as const;

// 🎯 CALENDAR EVENT TEXT COLORS - Dark versions matching background colors
// Dark text colors for good contrast on pastel backgrounds
export const calendarEventTextColors = {
  blue: '#1e40af',        // blue-800 - dark blue for blue-100 background
  cyan: '#155e75',        // cyan-800 - dark cyan for cyan-100 background
  amber: '#92400e',       // amber-800 - dark amber for amber-100 background
  yellow: '#854d0e',      // yellow-800 - dark yellow for yellow-100 background
  orange: '#9a3412',      // orange-800 - dark orange for orange-100 background
  rose: '#9f1239',        // rose-800 - dark rose for rose-100 background
  emerald: '#065f46',     // emerald-800 - dark emerald for emerald-100 background
  green: '#166534',       // green-800 - dark green for green-100 background
  red: '#991b1b',         // red-800 - dark red for red-100 background
  violet: '#5b21b6',      // violet-800 - dark violet for violet-100 background
  purple: '#6b21a8',      // purple-800 - dark purple for purple-100 background
  
  // Default fallback
  default: '#1e40af',     // blue-800
} as const;

// Helper function to get text color from background color
export const getCalendarEventTextColor = (backgroundColor: string): string => {
  // Create a mapping of background colors to text colors
  const colorMap: Record<string, string> = {
    [calendarEventColors.blue]: calendarEventTextColors.blue,
    [calendarEventColors.cyan]: calendarEventTextColors.cyan,
    [calendarEventColors.amber]: calendarEventTextColors.amber,
    [calendarEventColors.yellow]: calendarEventTextColors.yellow,
    [calendarEventColors.orange]: calendarEventTextColors.orange,
    [calendarEventColors.rose]: calendarEventTextColors.rose,
    [calendarEventColors.emerald]: calendarEventTextColors.emerald,
    [calendarEventColors.green]: calendarEventTextColors.green,
    [calendarEventColors.red]: calendarEventTextColors.red,
    [calendarEventColors.violet]: calendarEventTextColors.violet,
    [calendarEventColors.purple]: calendarEventTextColors.purple,
  };
  
  return colorMap[backgroundColor] || calendarEventTextColors.default;
};
